import React, { Component } from "react";
import PropTypes from "prop-types";
import { Card, Alert, Badge, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

class DuplicationInfo extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('DuplicationInfo component error:', error, errorInfo);
    }

    componentDidMount() {
        try {
            const { applicantId, getApplicantDuplicationInfo } = this.props;
            
            if (applicantId && getApplicantDuplicationInfo) {
                // Make API call but don't let it break the component if it fails
                getApplicantDuplicationInfo(applicantId).catch(error => {
                    console.warn('Failed to fetch duplication info:', error);
                    // Don't set error state, just log it
                });
            }
        } catch (error) {
            console.error('Error in DuplicationInfo componentDidMount:', error);
            // Don't set error state, just log it
        }
    }

    // Helper function to format dates safely
    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Helper function to get match type badge - handles comma-separated string format
    getMatchTypeBadge = (matchType) => {
        if (!matchType) {
            return (
                <Badge variant="secondary" title="No match">
                    <FontAwesomeIcon icon="question-circle" className="mr-1" />
                    None
                </Badge>
            );
        }
        
        // Handle comma-separated string format like "Resume, Email, Phone"
        const matchTypes = typeof matchType === 'string' 
            ? matchType.split(',').map(type => type.trim())
            : [matchType];
        
        const badges = [];
        
        matchTypes.forEach(type => {
            switch (type.toLowerCase()) {
                case 'resume':
                    badges.push(
                        <Badge key="resume" variant="danger" className="mr-1" title="Resume match">
                            <FontAwesomeIcon icon="file" className="mr-1" />
                            Resume
                        </Badge>
                    );
                    break;
                case 'email':
                    badges.push(
                        <Badge key="email" variant="warning" className="mr-1" title="Email match">
                            <FontAwesomeIcon icon="at" className="mr-1" />
                            Email
                        </Badge>
                    );
                    break;
                case 'phone':
                    badges.push(
                        <Badge key="phone" variant="info" className="mr-1" title="Phone match">
                            <FontAwesomeIcon icon="phone" className="mr-1" />
                            Phone
                        </Badge>
                    );
                    break;
                case 'name':
                    badges.push(
                        <Badge key="name" variant="success" className="mr-1" title="Name match">
                            <FontAwesomeIcon icon="user" className="mr-1" />
                            Name
                        </Badge>
                    );
                    break;
                case 'none':
                    badges.push(
                        <Badge key="none" variant="secondary" className="mr-1" title="No match">
                            <FontAwesomeIcon icon="question-circle" className="mr-1" />
                            None
                        </Badge>
                    );
                    break;
                default:
                    badges.push(
                        <Badge key="unknown" variant="secondary" className="mr-1" title={`Unknown match type: ${type}`}>
                            <FontAwesomeIcon icon="question-circle" className="mr-1" />
                            {type}
                        </Badge>
                    );
            }
        });
        
        return badges.length > 0 ? badges : (
            <Badge variant="secondary" title="Unknown match type">
                <FontAwesomeIcon icon="question-circle" className="mr-1" />
                Unknown
            </Badge>
        );
    };


    renderExEmployeeInfo() {
        const { loading, duplicationInfo } = this.props;
        
        if (loading) {
            return (
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">
                            <FontAwesomeIcon icon="user" className="mr-2" />
                            Ex-Employee Information
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" className="mr-2" />
                            Loading ex-employee information...
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        if (!duplicationInfo || !duplicationInfo.exEmployee) {
            return (
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">
                            <FontAwesomeIcon icon="user" className="mr-2" />
                            Ex-Employee Information
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <Alert variant="info" className="mb-0">
                            <FontAwesomeIcon icon="info-circle" className="mr-2" />
                            No ex-employee match found for this applicant.
                        </Alert>
                    </Card.Body>
                </Card>
            );
        }

        const exEmployee = duplicationInfo.exEmployee;
        
        return (
            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <FontAwesomeIcon icon="user" className="mr-2" />
                        Ex-Employee Match
                    </h6>
                    {this.getMatchTypeBadge(exEmployee.matchType)}
                </Card.Header>
                <Card.Body>
                    <div className="row">
                        <div className="col-md-6">
                            <div><strong>Employee Name:</strong> {exEmployee.employeeName || 'N/A'}</div>
                            <div><strong>Employee Email:</strong> {exEmployee.employeeEmail || 'N/A'}</div>
                            <div><strong>Employee ID:</strong> {exEmployee.employeeId || 'N/A'}</div>
                        </div>
                        <div className="col-md-6">
                            <div><strong>Date Started:</strong> {this.formatDate(exEmployee.employeeDateStarted)}</div>
                            <div><strong>Date Ended:</strong> {this.formatDate(exEmployee.lastWorkedDate)}</div>
                            <div><strong>Detected:</strong> {this.formatDate(exEmployee.detectedUtc)}</div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    renderDuplicatesInfo() {
        const { loading, duplicationInfo } = this.props;
        
        if (loading) {
            return (
                <Card>
                    <Card.Header>
                        <h6 className="mb-0">Duplicate Applicants</h6>
                    </Card.Header>
                    <Card.Body>
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" className="mr-2" />
                            Loading duplicate information...
                        </div>
                    </Card.Body>
                </Card>
            );
        }

        if (!duplicationInfo || !duplicationInfo.duplicates || duplicationInfo.duplicates.length === 0) {
            return (
                <Card>
                    <Card.Header>
                        <h6 className="mb-0">Duplicate Applicants</h6>
                    </Card.Header>
                    <Card.Body>
                        <Alert variant="info" className="mb-0">
                            <FontAwesomeIcon icon="info-circle" className="mr-2" />
                            No duplicate applicants found for this applicant.
                        </Alert>
                    </Card.Body>
                </Card>
            );
        }

        const duplicates = duplicationInfo.duplicates;
        
        return (
            <Card>
                <Card.Header>
                    <h6 className="mb-0">Duplicate Applicants ({duplicates.length})</h6>
                </Card.Header>
                <Card.Body>
                    <Table responsive striped hover>
                        <thead>
                            <tr>
                                <th>Duplicate Applicant</th>
                                <th>Match Type</th>
                                <th>Job Posting</th>
                                <th>Applied Date</th>
                                <th>Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {duplicates.map((duplicate, index) => (
                                <tr key={duplicate.id || index}>
                                    <td>
                                        <div>
                                            <strong>{duplicate.duplicateApplicantName || 'N/A'}</strong>
                                            {duplicate.duplicateApplicantId && (
                                                <span className="text-muted ml-1">
                                                    (ID: <Link to={`/hr/applicants/${duplicate.duplicateApplicantId}`} className="text-primary">
                                                        {duplicate.duplicateApplicantId}
                                                    </Link>)
                                                </span>
                                            )}
                                            <br />
                                            <small className="text-muted">{duplicate.duplicateApplicantEmail || 'N/A'}</small>
                                            {duplicate.duplicateApplicantJobPostingTitle && (
                                                <>
                                                    <br />
                                                    <small className="text-muted">
                                                        <strong>Job:</strong> {duplicate.duplicateApplicantJobPostingTitle}
                                                    </small>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                    <td>{this.getMatchTypeBadge(duplicate.matchType)}</td>
                                    <td>{duplicate.duplicateApplicantJobPostingTitle || 'N/A'}</td>
                                    <td>{this.formatDate(duplicate.duplicateApplicantAppliedDate || duplicate.detectedUtc)}</td>
                                    <td>{duplicate.duplicateApplicantOverallScore || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    }

    render() {
        try {
            // If there's an error state, show error UI
            if (this.state.hasError) {
                return (
                    <Alert variant="danger">
                        <FontAwesomeIcon icon="exclamation" className="mr-2" />
                        Something went wrong with the duplication information. Please refresh the page.
                    </Alert>
                );
            }

            const { error, loading, duplicationInfo } = this.props;
            
            if (error) {
                return (
                    <Alert variant="danger">
                        <FontAwesomeIcon icon="exclamation" className="mr-2" />
                        Error loading duplication information: {error}
                    </Alert>
                );
            }

            return (
                <div>
                    {this.renderExEmployeeInfo()}
                    <div className="mt-3">
                        {this.renderDuplicatesInfo()}
                    </div>
                </div>
            );
        } catch (error) {
            console.error('Error rendering DuplicationInfo:', error);
            return (
                <Alert variant="danger">
                    <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
                    An error occurred while rendering duplication information. Please refresh the page.
                </Alert>
            );
        }
    }
}

DuplicationInfo.propTypes = {
    applicantId: PropTypes.number.isRequired,
    duplicationInfo: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
    getApplicantDuplicationInfo: PropTypes.func.isRequired
};

export default DuplicationInfo;
